import { DateTimeAdapter } from './date-time-adapter';
import { DateTimeRange } from '../types';

/** Adapts type `D` to be usable as a date by cdk-based components that work with dates. */
export abstract class RangeDateTimeAdapter<D> {
  dateTimeAdapter: DateTimeAdapter<D>;
  protected locale: any;
  setLocale(locale: any) {
    this.locale = locale;
  }
  /**
   * Parses a range date from a user-provided value.
   * @param value The range value to parse.
   * @param parseFormat The expected format of the date value being parsed
   *     (type is implementation-dependent).
   * @param separator The string between 2 dates
   *     (type is implementation-dependent).
   * @returns The parsed date.
   */
  abstract parse(value: any, parseFormat: string, separator?: any): DateTimeRange<D> | null;

  /**
   * Formats a date as a string according to the given format.
   * @param range The range value to format.
   * @param displayFormat The format to use to display the date as a string.
   * @param separator The string between 2 dates
   * @returns The formatted date string.
   */
  format = (range: DateTimeRange<D>, displayFormat: string, separator = ' - ') =>
    this.isRangeInstance(range)
      ? this.dateTimeAdapter.format(range.start, displayFormat) +
        separator +
        this.dateTimeAdapter.format(range.end, displayFormat)
      : '';

  /**
   * Checks whether the given object is considered a range instance by this RangeAdapter.
   * @param obj The object to check
   * @returns Whether the object is a date instance.
   */
  isRangeType(obj: object): boolean {
    return (
      obj instanceof Object &&
      obj.hasOwnProperty('start') && // eslint-disable-line no-prototype-builtins
      obj.hasOwnProperty('end') // eslint-disable-line no-prototype-builtins
    );
  }
  /**
   * Checks whether the given object is considered a range instance by this RangeAdapter.
   * @param obj The object to check
   * @returns Whether the object is a date instance.
   */
  // isRangeInstance(obj: Object): boolean {
  isRangeInstance(obj: object): boolean {
    return (
      this.isRangeType(obj) &&
      this.dateTimeAdapter.isDateInstance(obj['start']) &&
      this.dateTimeAdapter.isValid(obj['start']) &&
      this.dateTimeAdapter.isDateInstance(obj['end']) &&
      this.dateTimeAdapter.isValid(obj['end'])
    );
  }
  /**
   * Checks whether the given object is considered an empty range instance by this RangeAdapter.
   * @param obj The object to check
   * @returns Whether the object is a date instance.
   */
  isEmpty(obj: object): boolean {
    return obj === null || (this.isRangeType(obj) && !obj['start'] && !obj['end']);
  }

  /**
   * Checks whether the given object is considered an incomplete range instance by this
   * RangeAdapter.
   * @param obj The object to check
   * @returns Whether the object is a date instance.
   */
  isIncomplete(obj: object): boolean {
    return this.isRangeType(obj) && (!obj['start'] || !obj['end']);
  }

  /**
   * Checks whether the given date is valid.
   * @param range The date to check.
   * @returns Whether the date is valid.
   */
  isValidRange(range: DateTimeRange<D>): boolean {
    return this.isRangeInstance(range) && this.dateTimeAdapter.compareDateTime(range.start, range.end) <= 0;
  }

  /**
   * Checks if two ranges are equal.
   * @param first The first datetime to check.
   * @param second The second datetime to check.
   * @returns Whether the two datetime are equal.
   *     Null dates are considered equal to other null dates.
   */
  sameRange(first: DateTimeRange<D>, second: DateTimeRange<D>): boolean {
    if (!this.isValidRange(first) || !this.isValidRange(second)) {
      return false;
    }
    return (
      this.dateTimeAdapter.sameDateTime(first.start, second.start) &&
      this.dateTimeAdapter.sameDateTime(first.end, second.end)
    );
  }
  /**
   * Clamp the given range between min and max dates.
   * @param range The date to clamp.
   * @param min The minimum value to allow. If null or omitted no min is enforced.
   * @param max The maximum value to allow. If null or omitted no max is enforced.
   * @returns intersection range
   */
  clampRange(range: DateTimeRange<D>, min?: D | null, max?: D | null): DateTimeRange<D> {
    return {
      start: this.dateTimeAdapter.clampDate(range.start, min, max),
      end: this.dateTimeAdapter.clampDate(range.end, min, max)
    };
  }

  /**
   * Attempts to deserialize a value to a valid range object. This is different from parsing in that
   * deserialize should only accept non-ambiguous, locale-independent formats (e.g. a ISO 8601
   * string). The default implementation does not allow any deserialization, it simply checks that
   * the given value is already a valid range object or null. It is therefore possible to
   * support passing values from your backend directly to these properties by overriding this method
   * to also deserialize the format used by your backend.
   * @param range The value to be deserialized into a range object.
   * @returns The deserialized date object, either a valid range, null if the value can be
   *     deserialized into a null range (e.g. the empty string), or an empty range.
   */
  deserialize(range: any): DateTimeRange<D> | null {
    if (range === null || (this.isRangeInstance(range) && this.isValidRange(range))) {
      return range;
    }
    return this.empty();
  }

  empty(): DateTimeRange<D> {
    return { start: null, end: null };
  }

  /**
   * @param obj The object to check.
   * @returns The given object if it is both a range instance and valid, otherwise null.
   */
  validOrNull(obj: any): DateTimeRange<D> | null {
    return this.isRangeInstance(obj) && this.isValidRange(obj) ? obj : null;
  }
}
